import axios from "../axiosConfig";
import { ENDPOINT_ADMIN_USER_RESET, ENDPOINT_UPDATE_INVITE_PREF, ENDPOINT_USER_RETRIEVE_ROLES, ENDPOINT_USER_ROLE, ENDPOINT_USER_UPDATE, ENDPOINT_USERS, ENDPOINT_USER_REMOVE_COMPANY_ACCESS, ENDPOINT_USER_FORCE_DELETE_REQUEST_OTP, ENDPOINT_USER_FORCE_DELETE } from "../../constants";
import { AppRole } from "../reg/actions";

export type RoleDef = {
    id: string
    name: string
    desc: string
}

export type AppRoleDef = {
    id: string   // app id
    name: string
    roles: RoleDef[] | null
}

export type UserAccess = {
    app: string
    code: string
    email: string
    status: string
    com: string
    userid: string
    username: string
    roles: string[]
}

export type UserInfo = {
    email: string
    userid?: string
    pref?: ComPref | null
    access: UserAccess[]
    username?: string
    status: string; // Added overall user status
}

export type UserResponse = {
    total: number
    // Updated to reflect the backend response structure including overall_status
    users: (UserAccess & { overall_status: string })[] 
    prefs: ComPref[] | null
}

export type AppRoleResponse = {
    global: RoleDef[]
    apps: AppRoleDef[]
}

export type ComPref = {
    fname: string
    lname: string
    userid: string
}

export function retrieveRoleDefs() : Promise<AppRoleResponse> {
    return new Promise<AppRoleResponse>( async (resolve, reject) => {
        axios.get(ENDPOINT_USER_RETRIEVE_ROLES, {withCredentials:true}).then( res => {
            console.log(res.data)
            if (res.data.Code !== "200")
                throw new Error(res.data.Code)
            resolve( res.data.Msg)
        })
        .catch( (err) => {
            console.log(err)
            reject(err)
        });
    })
}

export async function adminInitiateUserPasswordResetAPI(username: string): Promise<void> {
    // Assuming axios is configured with a base URL like /api
    await axios.post(`${ENDPOINT_ADMIN_USER_RESET}`, {"username":username}, { withCredentials: true });
}

export function retrieveCompanyUsers(params: {
    offset: number,
    prefs: boolean,
    search?: string,
    branchFilter?: string,
    appFilter?: string,
    // Only honoured for core1 admins while cross-company browsing is enabled;
    // the server re-checks on every request.
    com?: string
}) : Promise<UserResponse> {
    return new Promise<UserResponse>( async (resolve, reject) => {
        axios.post(ENDPOINT_USERS, {
            offset: params.offset,
            prefs: params.prefs,
            search: params.search || "",
            branchFilter: params.branchFilter || "",
            appFilter: params.appFilter || "",
            com: params.com || ""
        }, {
            withCredentials:true
        }).then( res => {
            console.log(res.data)
            if (res.data.Code !== "200")
                throw new Error(res.data.Code)
            resolve( res.data.Msg)
        })
        .catch( (err) => {
            console.log(err)
            reject(err)
        });
    })
}

export function updateRoles(email:string,roleList:AppRole[]) : Promise<any> {
    return new Promise<string>( async (resolve, reject) => {
        axios.post(ENDPOINT_USER_ROLE, {
            email,
            data: roleList
        },{
            withCredentials:true
        }).then( res => {
            console.log(res.data)
            resolve( res.data)
        })
        .catch( (err) => {
            console.log(err)
            reject(err)
        });
    })
}

export function updateInvitePref(lname="",fname="",userid:string): Promise<string> {
    return new Promise<any>( async (resolve, reject) => {
        axios.post(ENDPOINT_UPDATE_INVITE_PREF, {
            lname,
            fname,
            userid,
        }, {withCredentials:true}).then( res => {
            console.log(res.data)
            resolve(res.data.Code)
        })
        .catch( (err) => {
            console.log(err)
            reject(err)
        });
    })
}

export function removeUserCompanyAccess(email: string): Promise<string> {
    return new Promise<string>(async (resolve, reject) => {
        axios.post(ENDPOINT_USER_REMOVE_COMPANY_ACCESS, {
            email
        }, {
            withCredentials: true
        }).then(res => {
            console.log(res.data)
            resolve(res.data.Code)
        })
        .catch((err) => {
            console.log(err)
            reject(err)
        });
    })
}

// A company left without an owner by a force delete. The server returns these instead
// of a bare refusal so the operator can be told exactly what is blocking.
export type OwnerConstraint = {
    com: string
    reason: string
}

export type ForceDeleteError = Error & { code?: string, blocking?: OwnerConstraint[] }

function forceDeleteError(code: string, blocking?: OwnerConstraint[]): ForceDeleteError {
    const err = new Error(code) as ForceDeleteError
    err.code = code
    err.blocking = blocking
    return err
}

// Step one of a force delete: proves the operator's password and emails them a code.
// Pass token instead of password to resend a code for an already-verified request, so
// the password never has to be held in component state.
export function requestForceDeleteOtp(email: string, password?: string, token?: string): Promise<string> {
    return new Promise<string>(async (resolve, reject) => {
        axios.post(ENDPOINT_USER_FORCE_DELETE_REQUEST_OTP, {
            email,
            password: password ? btoa(password) : "",
            token: token || ""
        }, { withCredentials: true }).then(res => {
            if (res.data.Code !== "200") {
                reject(forceDeleteError(res.data.Code, res.data.Msg?.blocking))
                return
            }
            resolve(res.data.Msg?.token)
        })
        .catch((err) => {
            reject(forceDeleteError(err?.response?.data?.Code || "internal_error"))
        });
    })
}

// Step two: purges the user. The target is bound to the token server-side, so it
// cannot be redirected at a different user after the code was issued.
export function forceDeleteUser(token: string, otp: string): Promise<any> {
    return new Promise<any>(async (resolve, reject) => {
        axios.post(ENDPOINT_USER_FORCE_DELETE, {
            token,
            otp
        }, { withCredentials: true }).then(res => {
            if (res.data.Code !== "200") {
                reject(forceDeleteError(res.data.Code, res.data.Msg?.blocking))
                return
            }
            resolve(res.data.Msg?.report)
        })
        .catch((err) => {
            reject(forceDeleteError(err?.response?.data?.Code || "internal_error"))
        });
    })
}
